import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { BackButton, NextButton } from '../../../common/buttonsComponents';
import SearchDropDown from '../../../DropDown/SearchDropDown/SearchDropDown';
import Loading, { LoadingWrapper } from 'components/LoadingComponent/LoadingComponent';
import { showErrorPopup } from 'components/popups/ErrorPopup/ErrorPopup';
import {
  getDecisionNodeAtPath,
  getSimplrAccountIdOfSelectedTicket,
  getSelectedOrderFromOrdersSearch,
} from '../../../../extractFromState';
import { getSelectedLineItems } from '../../../../selectors/tools';
import { addEdgeToDecisionTreePath, returnToPreviousMacroWizardPath } from '../../../../actions/searchActions';
import { replaceOrderItemsViaCancelRoute } from '../../../../actions/tools/CommonAsyncOrderActions';
import { setLoading } from '../../../../actions';
import * as ordersAPI from '../../../../api/orders';
import { showError } from '../../../../util/errorPopupUtil';
import { makeReadableList } from '../../../../util/formatUtil';
import { TopContainer, Title, ReasonContainer, Placeholder } from './DTReplaceItemThenSendMacroNode.styles';
import DTImageSelector from '../../Common/DTImageSelector/DTImageSelector';
import { withConfirmation } from '../../DTConfirmationView/withConfirmation';
import { BlankReasonContainer } from '../../Common/DTSelectReason/DTSelectReason.styles';
import { REASON_TYPES } from 'constants/decisionTree';
import { ActionsButtonsWrapper } from '../../DecisionTree.styles';

const DTReplaceItemThenSendMacroNode = () => {
  const dispatch = useDispatch();
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const selectedLineItems = useSelector(getSelectedLineItems);
  const selectedOrder = useSelector(getSelectedOrderFromOrdersSearch);
  const orderId = get(useSelector(getSelectedOrderFromOrdersSearch), 'orderId');
  const domain = get(useSelector(getSelectedOrderFromOrdersSearch), 'simplrParams.domain');
  const simplrAccountId = useSelector(getSimplrAccountIdOfSelectedTicket);
  const isLoading = useSelector(
    (state) => state.ui.loading.isLoading && state.ui.loading.component === 'orderCancelContainer'
  );

  const [replaceReasons, setReplaceReasons] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [isValidForReplace, setIsValidForReplace] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  const isReplaceReasonsRequired = !isEmpty(replaceReasons);

  useEffect(() => {
    const allowUnfulfilledReplacements = get(decisionNode, 'unfulfilledReplacements', false);
    const replacedItems = selectedLineItems.filter((item) => get(item, 'isReplaced'));
    const replacedItemNames = replacedItems.map((item) => get(item, 'itemName'));
    const isFulfilled = ['fulfilled', 'partially-fulfilled'].includes(get(selectedOrder, 'orderStatus'));
    const isFullyReplaced = !isEmpty(replacedItems) && replacedItems.length === selectedLineItems.length;
    const isPartiallyReplaced = !isEmpty(replacedItems) && replacedItems.length < selectedLineItems.length;

    if (isFullyReplaced) {
      dispatch(addEdgeToDecisionTreePath('all_items_already_replaced'));

      return;
    }

    if (isPartiallyReplaced) {
      const prefixPopupText = replacedItemNames.length > 1 ? 'These items have' : 'This item has';
      showErrorPopup(`${prefixPopupText} already been replaced: ${makeReadableList(replacedItemNames)}`, 'Sorry');
      dispatch(addEdgeToDecisionTreePath('some_items_already_replaced'));

      return;
    }

    if (isFulfilled || allowUnfulfilledReplacements) {
      // NOTE: The current code will allow invalid statuses into the block if
      // allowUnfulfilledReplacements = true. This is by design. Ops will use info widgets
      // to filter invalid orders out before they reach this component
      return setIsValidForReplace(true);
    }

    dispatch(addEdgeToDecisionTreePath('replacement_order_invalid'));
  }, [decisionNode, dispatch, selectedLineItems, selectedOrder]);

  useEffect(() => {
    (async () => {
      if (isValidForReplace) {
        try {
          dispatch(setLoading(true, 'orderCancelContainer'));

          const params = {
            simplrAccountId,
            type: REASON_TYPES.REPLACE,
          };

          const { reasons } = await ordersAPI.getReasons(params);
          if (reasons && !isEmpty(reasons)) {
            // for use in the dropdown list
            const options = reasons.map((reason) => ({
              ...reason,
              value: reason.id,
            }));

            setReplaceReasons(options);
          } else {
            setReplaceReasons([]);
          }
        } catch (error) {
          showError(error);
        } finally {
          dispatch(setLoading(false, 'orderCancelContainer'));
        }
      }
    })();
  }, [dispatch, simplrAccountId, isValidForReplace]);

  const handleReasonChange = (reason) => {
    setSelectedReason(reason);
    setSelectedImages([]);
  };

  const isReplaceButtonActive = () => {
    const isImageOptional = get(selectedReason, 'isImageOptional');

    if (!!selectedReason && isImageOptional) {
      return true;
    } else if (!!selectedReason && !isImageOptional && selectedImages.length > 0) {
      return true;
    } else if (!isReplaceReasonsRequired) {
      return true;
    }

    return false;
  };

  const handleReplaceButtonClick = () => {
    dispatch(
      replaceOrderItemsViaCancelRoute(
        simplrAccountId,
        orderId,
        selectedLineItems,
        selectedReason,
        selectedImages,
        domain
      )
    );
  };

  const renderActionButtons = () => {
    return (
      <ActionsButtonsWrapper>
        <NextButton
          disabled={!isReplaceButtonActive()}
          active={isReplaceButtonActive()}
          type="submit"
          onClick={handleReplaceButtonClick}
        >
          REPLACE
        </NextButton>
        <BackButton onClick={() => dispatch(returnToPreviousMacroWizardPath())}>BACK</BackButton>
      </ActionsButtonsWrapper>
    );
  };

  return (
    <>
      {isLoading && (
        <LoadingWrapper>
          <Loading delay={0} />
        </LoadingWrapper>
      )}
      <TopContainer>
        {!isLoading && replaceReasons && isReplaceReasonsRequired ? (
          <>
            <ReasonContainer>
              <Title>Select a reason for the replacement</Title>
              <SearchDropDown
                selectedId={selectedReason}
                placeholder="Select a reason"
                options={replaceReasons}
                onChange={handleReasonChange}
              />
            </ReasonContainer>
            {selectedReason && !get(selectedReason, 'isImageOptional', true) ? (
              <DTImageSelector selectedImages={selectedImages} onSelect={setSelectedImages} />
            ) : (
              <Placeholder />
            )}
          </>
        ) : (
          <BlankReasonContainer />
        )}
        {renderActionButtons()}
      </TopContainer>
    </>
  );
};

export default withConfirmation(DTReplaceItemThenSendMacroNode);
